// import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import React, { useState, useCallback, useEffect, useRef, useMemo, useId } from "react";
import DatePicker from "react-datepicker";
// import TableChartIcon from "@mui/icons-material/TableChart";
import { useNavigate } from "react-router-dom";
// import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroups } from "../../../../../store/slices/groupSlice";
import {
    setProbabilityToDateRedux, setProbabilityFromDateRedux,
    setProbabilityGroupRedux,
} from "../../../../../store/slices/filterSlice";
import { setFilteredNavData } from "../../../../../store/slices/LeadreportfilteredSlice";
// import RemoveIcon from '@mui/icons-material/Remove';
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import DateConversion from "../../../../../utils/DateConversion";
import CommaConvert from "../../../../../utils/CommaConvert";

import { ChevronDown } from "lucide-react"
import { ChartPie } from "lucide-react"
import { TableOfContents } from "lucide-react"
// import { Download } from "lucide-react"
import { Calendar } from "lucide-react"
import { hasViewOrFullAccess, getExactFeatureAccessLevel } from "../../../../../utils/featureAccessUtils";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import DownloadReport from "../../../../../pages/lead/DownloadReport";
import { setDownloadReportTitle } from "../../../../../store/slices/pageSlice";

const getProbColor = (prob) => {
    switch (prob?.toLowerCase()) {
        case "high":
            return "#15803d";
        case "medium":
            return "#fb923c";
        case "low":
            return "#f87171";
        default:
            return "#2563eb";
    }
};

const PROB_CHART_ORDER = ["high", "medium", "low"];

const PROB_PIE_SLICE_ORDER = ["low", "medium", "high"];

const ProbabilityRechartsDonut = ({ segments, onSegmentClick }) => {
    const gradId = useId().replace(/:/g, "");

    const activeSegments = useMemo(
        () => segments.filter((s) => parseFloat(s.percentage) > 0),
        [segments]
    );

    const capFirst = (s) => {
        const str = String(s ?? "").trim();
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const pieData = useMemo(() => {
        if (!activeSegments.length) {
            return [{ name: "__empty", value: 100, prob: null, isRest: true }];
        }
        const byProb = new Map(
            activeSegments.map((s) => [String(s.prob || "").toLowerCase(), s])
        );
        const ordered = [];
        for (const key of PROB_PIE_SLICE_ORDER) {
            const s = byProb.get(key);
            if (s) ordered.push(s);
        }
        activeSegments.forEach((s) => {
            const k = String(s.prob || "").toLowerCase();
            if (!PROB_PIE_SLICE_ORDER.includes(k)) ordered.push(s);
        });
        const rows = ordered.map((s) => ({
            name: s.prob,
            value: parseFloat(s.percentage) || 0,
            prob: s.prob,
        }));
        const sum = rows.reduce((a, b) => a + b.value, 0);
        if (sum > 0 && sum < 100) {
            rows.push({
                name: "__rest",
                value: 100 - sum,
                prob: null,
                isRest: true,
            });
        }
        return rows;
    }, [activeSegments]);

    const fillForEntry = (entry) => {
        if (entry.isRest) return "#e5e7eb";
        const k = entry.prob?.toLowerCase();
        if (k === "medium") return `url(#probGradMedium-${gradId})`;
        if (k === "low") return `url(#probGradLow-${gradId})`;
        if (k === "high") return "#15803d";
        return getProbColor(entry.prob);
    };

    // (Labels on the pie are intentionally disabled; percentages are shown in the side legend.)

    const handlePieClick = (sector) => {
        const row = sector?.payload ?? sector;
        if (!row || row.isRest || !row.prob || !onSegmentClick) return;
        onSegmentClick(row.prob);
    };

    return (
        <div className="relative flex justify-center items-center drop-shadow-[0_8px_20px_rgba(0,0,0,0.15)]">
            <div className="relative h-[240px] w-[240px] shrink-0">
                <PieChart width={240} height={240}>
                    <defs>
                        <linearGradient id={`probGradMedium-${gradId}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#FDBA74" />
                            <stop offset="100%" stopColor="#F97316" />
                        </linearGradient>
                        <linearGradient id={`probGradLow-${gradId}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#F87171" />
                            <stop offset="100%" stopColor="#EF4444" />
                        </linearGradient>
                    </defs>
                    <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        formatter={(val, _name, props) => {
                            const v = Number(val);
                            const pct = Number.isFinite(v) ? `${v.toFixed(2)}%` : `${val}%`;
                            const label =
                                props?.payload?.prob
                                    ? capFirst(props.payload.prob)
                                    : props?.payload?.name && props.payload.name !== "__rest"
                                      ? capFirst(props.payload.name)
                                      : "Other";
                            return [pct, label];
                        }}
                        contentStyle={{
                            borderRadius: 10,
                            border: "1px solid rgba(0,0,0,0.08)",
                            boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
                        }}
                        itemStyle={{ color: "#111827", fontWeight: 600 }}
                        labelStyle={{ color: "#6B7280", fontWeight: 600 }}
                    />
                    <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx={120}
                        cy={120}
                        // Slightly thinner ring for better balance.
                        innerRadius={72}
                        outerRadius={112}
                        startAngle={90}
                        endAngle={-270}
                        paddingAngle={0}
                        stroke="#ffffff"
                        strokeWidth={1}
                        onClick={handlePieClick}
                        labelLine={false}
                        label={false}
                    >
                        {pieData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={fillForEntry(entry)}
                                cursor={entry.isRest ? "default" : "pointer"}
                            />
                        ))}
                    </Pie>
                </PieChart>
            </div>
        </div>
    );
};

const ProbabilityStatus = ({ currentEmployee, hasNoFeatureConfig, userLevel }) => {
    const dispatch = useDispatch();
    const currency = useCurrencySymbol();
    const { getDatePlaceholder, getDateFormat } = DateConversion();
    const USAComma = CommaConvert();

    const downloadAccessLevel = !hasNoFeatureConfig && currentEmployee
        ? (
            getExactFeatureAccessLevel(currentEmployee, "PROBABILITY STATUS DOWNLOAD")
            ?? getExactFeatureAccessLevel(currentEmployee, "PROBABILITY STATUS GRAPHS")
        )
        : null;
    const isDownloadViewOnly = downloadAccessLevel && String(downloadAccessLevel).trim().toUpperCase() === "VIEW ONLY";

    const canShowGraphs = userLevel === "L0" || hasNoFeatureConfig || !currentEmployee || hasViewOrFullAccess(currentEmployee, "PROBABILITY STATUS GRAPHS");
    const canShowFilter = userLevel === "L0" || hasNoFeatureConfig || !currentEmployee || hasViewOrFullAccess(currentEmployee, "PROBABILITY STATUS FILTER");
    const sendDownlodTile = () => {
        if (isDownloadViewOnly) return;
        dispatch(setDownloadReportTitle("ProbabilityStatusReport"));
    };

    const [probabilityDownload, setProbabilityDownload] = useState([]);
    const [showGroupDropdown, setShowGroupDropdown] = useState(false);

    const [filteredLeadReportsLead, setFilteredLeadReportsLead] = useState([]);

    const { filteredReports } = useSelector((state) => state.filteredData);
    const { group } = useSelector((state) => state.group);
    const groupNameById = useMemo(() => {
        const normalize = (value) => String(value ?? "").trim();
        const map = new Map();
        (Array.isArray(group) ? group : []).forEach((groupItem) => {
            const idA = normalize(groupItem?._id);
            const idB = normalize(groupItem?.id);
            const name = normalize(
                groupItem?.name ??
                    groupItem?.newGroupName ??
                    groupItem?.groupName ??
                    groupItem?.GroupName ??
                    ""
            );
            const label = name || idA || idB;
            if (idA) map.set(idA, label || idA);
            if (idB && idB !== idA) map.set(idB, label || idB);
        });
        return map;
    }, [group]);
    const groupIdByName = useMemo(() => {
        const map = new Map();
        groupNameById.forEach((name, id) => map.set(String(name).trim(), id));
        return map;
    }, [groupNameById]);
    const getLeadGroupId = useCallback((lead) => {
        const normalize = (value) => String(value ?? "").trim();
        const fromObject = normalize(lead?.groupid?._id ?? lead?.groupid?.id);
        if (fromObject) return fromObject;
        const fromString = typeof lead?.groupid === "string" ? normalize(lead.groupid) : "";
        if (fromString && groupNameById.has(fromString)) return fromString;
        if (fromString && groupIdByName.has(fromString)) return groupIdByName.get(fromString);
        const fromAssigned = normalize(lead?.AssignedGroup);
        if (fromAssigned && groupIdByName.has(fromAssigned)) return groupIdByName.get(fromAssigned);
        return "";
    }, [groupNameById, groupIdByName]);
    const getLeadGroupName = useCallback((lead) => {
        const gid = getLeadGroupId(lead);
        if (gid && groupNameById.has(gid)) return groupNameById.get(gid);
        return String(lead?.AssignedGroup || "").trim();
    }, [getLeadGroupId, groupNameById]);

    const [showgraph, setShowgraph] = useState(true);

    const handleToggleTable = () => {
        setShowgraph(!showgraph);
    };
    useEffect(() => {
        if (Array.isArray(filteredReports)) {
            // const pendingLeads = filteredReports.filter(
            //     (lead) => lead.Status === "Pending"
            // );
            const pendingLeads = filteredReports.filter(
                (lead) => lead.Status?.toLowerCase() === "pending"
            );
            setFilteredLeadReportsLead(pendingLeads);
        }
    }, [filteredReports]);

    const dropdownRefGroup = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRefGroup.current &&
                !dropdownRefGroup.current.contains(event.target)
            ) {
                setShowGroupDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const popupRef = useRef(null);

    const [probabilityTab, setProbabilityTab] = useState("graph");

    const [AssignedGroup, setAssignedGroup] = useState("");
    const [probabilityStatus, setProbabilityStatus] = useState([]);
    const [probabilityFilterDateFrom, setProbabilityFilterDateFrom] = useState();
    const [probabilityFilterDateTo, setProbabilityFilterDateTo] = useState();
    console.log("probabilityStatus", probabilityStatus)
    const [selectedFromDate, setSelectedFromDate] = useState(null); // Temporary state
    const [selectedToDate, setSelectedToDate] = useState(null); // Temporary state

    const groupFilterRedux = useSelector((state) => state.filters.groupFilter);

    const [selectedGroup, setSelectedGroup] = useState(
        groupFilterRedux === "All" ? "" : groupFilterRedux || "");

    useEffect(() => {
        if (groupFilterRedux !== undefined && groupFilterRedux !== null) {
            const asId = groupIdByName.get(String(groupFilterRedux).trim()) || groupFilterRedux;
            setSelectedGroup(asId);
        } else { setSelectedGroup("All") }
    }, [groupFilterRedux, groupIdByName]);

    const handleProbabilityGroup = (groupId) => {
        if (!groupId) return;
        setSelectedGroup(groupId);
        if (groupId === "All") {
            // Set AssignedGroup to an empty string or a special value
            setSelectedGroup(groupIdByName.get(String(groupFilterRedux).trim()) || groupFilterRedux);
            setShowGroupDropdown(false);
            return;
        }
        setShowGroupDropdown(false);
        dispatch(setProbabilityGroupRedux(groupId));
    };

    const handleProbabilityFromDate = (date) => {
        if (!date) return;
        setSelectedFromDate(date);
        dispatch(setProbabilityFromDateRedux(date));
    };
    const handleProbabilityToDate = (date) => {
        if (!date) return;
        setSelectedToDate(date);
        dispatch(setProbabilityToDateRedux(date));
    };




    const probabilityFilterDateFromRedux = useSelector(
        (state) => state.filters.probabilityFilterDateFrom
    );
    const probabilityFilterToFromRedux = useSelector(
        (state) => state.filters.probabilityFilterToFrom
    );
    const probabilityGroupFilterRedux = useSelector(
        (state) => state.filters.probabilityGroupFilter
    );

    useEffect(() => {
        if (probabilityFilterDateFromRedux !== undefined && probabilityFilterDateFromRedux !== null) {
            setSelectedFromDate(probabilityFilterDateFromRedux);
        } else {
            setSelectedFromDate("")
        }
    }, [probabilityFilterDateFromRedux]);

    useEffect(() => {
        if (probabilityFilterToFromRedux !== undefined && probabilityFilterToFromRedux !== null) {
            setSelectedToDate(probabilityFilterToFromRedux);
        } else { setSelectedToDate("") }
    }, [probabilityFilterToFromRedux]);

    useEffect(() => {
        if (probabilityGroupFilterRedux) {
            const asId = groupIdByName.get(String(probabilityGroupFilterRedux).trim()) || probabilityGroupFilterRedux;
            setSelectedGroup(asId);
        }
    }, [probabilityGroupFilterRedux, groupIdByName]);

    const probabilityGroupDropdownLabel = useMemo(() => {
        const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());
        const sel = norm(selectedGroup);
        const gf = norm(groupFilterRedux);

        if (sel === "All" || (!sel && (!gf || gf === "All"))) return "All";

        if (sel && groupNameById.has(sel)) return groupNameById.get(sel);

        const idFromDashboard =
            (gf && groupNameById.has(gf) ? gf : null) ||
            (gf ? groupIdByName.get(gf) : null) ||
            sel ||
            gf;
        if (!idFromDashboard || idFromDashboard === "All") return "All";
        if (groupNameById.has(idFromDashboard)) return groupNameById.get(idFromDashboard);

        const idFromName = groupIdByName.get(idFromDashboard);
        if (idFromName && groupNameById.has(idFromName)) return groupNameById.get(idFromName);

        return sel || gf || "All";
    }, [selectedGroup, groupFilterRedux, groupNameById, groupIdByName]);

    const [probCalculatedCount, setProbCalculatedCount] = useState(0);
    const [calculatedPercentage, setCalculatedPercentage] = useState(0);
    const [totalProbabilityAmount, setTotalProbabilityAmount] = useState(0);
    const [, setTotalProbabilityCount] = useState(0);

    const aggregateProbabilityData = useCallback(
        (data) => {
            const probMap = new Map();
            let totalProbAmount = 0;
            const totalCount = data.length;
            const calculatedCount = data.length;
            setProbCalculatedCount(calculatedCount);

            data.forEach((LeadReport) => {
                let prob = LeadReport.Probability;
                if (!prob) return;
                prob = prob.toLowerCase();   // <-- MAKE CASE INSENSITIVE HERE
                const count = probMap.has(prob) ? probMap.get(prob).count + 1 : 1;
                const amount = parseFloat(LeadReport.QuotedValue);
                totalProbAmount += amount;

                probMap.set(prob, {
                    count,
                    amount: (probMap.get(prob)?.amount || 0) + amount,
                });
            });

            let potentialProbabilityArray = Array.from(
                probMap,
                ([prob, { count, amount }]) => ({
                    prob,
                    count,
                    amount,
                    percentage: (count / totalCount) * 100, // Store as a number
                })
            );

            // Calculate the total percentage
            let totalPercentage = potentialProbabilityArray.reduce(
                (sum, prob) => sum + prob.percentage,
                0
            );

            // Adjust the total percentage to 100% if counts match
            if (totalCount === calculatedCount) {
                const discrepancy = 100 - totalPercentage;
                totalPercentage = 100;

                // Adjust the largest segment to account for the discrepancy
                if (potentialProbabilityArray.length > 0) {
                    const maxIndex = potentialProbabilityArray.reduce(
                        (maxIdx, prob, idx, arr) =>
                            prob.percentage > arr[maxIdx].percentage ? idx : maxIdx,
                        0
                    );
                    potentialProbabilityArray[maxIndex].percentage += discrepancy;
                }
            }

            // Convert percentages to fixed decimal places for display
            potentialProbabilityArray = potentialProbabilityArray.map((prob) => ({
                ...prob,
                percentage: Number(prob.percentage).toFixed(2),
            }));

            setCalculatedPercentage(totalPercentage.toFixed(2));
            setProbabilityStatus(potentialProbabilityArray);
            setTotalProbabilityAmount(totalProbAmount);
            setTotalProbabilityCount(totalCount);
        },
        [
            setProbabilityStatus,
            setTotalProbabilityAmount,
            setTotalProbabilityCount,
        ]
    );

    useEffect(() => {
        if (filteredLeadReportsLead) {
            // const pendingLeads = filteredLeadReportsLead.filter(
            //     (lead) => lead.Status === "Pending"
            // );
            const pendingLeads = filteredLeadReportsLead.filter(
                (lead) => lead.Status?.toLowerCase() === "pending"
            );

            const sortedLeads = pendingLeads.sort(
                (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
            );

            aggregateProbabilityData(sortedLeads);
            setProbabilityDownload(sortedLeads);
        }
    }, [filteredLeadReportsLead, aggregateProbabilityData]);

    const navigate = useNavigate();

    const segmentsForChart = useMemo(
        () => {
            const sorted = PROB_CHART_ORDER.map((key) =>
                probabilityStatus.find((p) => p.prob?.toLowerCase() === key)
            ).filter(Boolean);
            const rest = probabilityStatus.filter(
                (p) => !PROB_CHART_ORDER.includes(p.prob?.toLowerCase())
            );
            return [...sorted, ...rest];
        },
        [probabilityStatus]
    );

    const legendItems = useMemo(() => {
        const items = [];
        const high = segmentsForChart.find((p) => p.prob?.toLowerCase() === "high");
        const med = segmentsForChart.find((p) => p.prob?.toLowerCase() === "medium");
        const low = segmentsForChart.find((p) => p.prob?.toLowerCase() === "low");
        if (high)
            items.push({
                key: "high",
                label: "High",
                dot: "bg-green-600",
                pct: high.percentage,
            });
        if (med)
            items.push({
                key: "medium",
                label: "Medium",
                dotGradient: "linear-gradient(135deg, #FDBA74 0%, #F97316 100%)",
                pct: med.percentage,
            });
        if (low)
            items.push({
                key: "low",
                label: "Low",
                dotGradient: "linear-gradient(135deg, #F87171 0%, #EF4444 100%)",
                pct: low.percentage,
            });
        segmentsForChart.forEach((p) => {
            const k = p.prob?.toLowerCase();
            if (!["high", "medium", "low"].includes(k)) {
                items.push({
                    key: p.prob,
                    label:
                        p.prob?.charAt(0).toUpperCase() +
                        (p.prob?.slice(1).toLowerCase() || ""),
                    dot: "bg-blue-500",
                    pct: p.percentage,
                });
            }
        });
        return items;
    }, [segmentsForChart]);

    useEffect(() => {
        dispatch(fetchGroups());
    }, [dispatch]);

    const probabilityNavigationl1 = useCallback(
        (clickedProb) => {
            // const filteredDataprobabilityl1 = probabilityDownload.filter(
            //     (item) => item.Probability === clickedProb
            // );
            const filteredDataprobabilityl1 = probabilityDownload.filter(
                (item) => item.Probability?.toLowerCase() === clickedProb?.toLowerCase()
            );
            dispatch(setFilteredNavData(filteredDataprobabilityl1));
            navigate(`/dashboard/lead-report`);
        },
        [probabilityDownload, navigate, dispatch]
    );

    const handleSubmit = () => {
        setAssignedGroup(selectedGroup); // Update AssignedGroup on submit
        setProbabilityFilterDateFrom(selectedFromDate);
        setProbabilityFilterDateTo(selectedToDate);
    };

    const handleFilter = useCallback(() => {
        // Run the filtering logic based on AssignedGroup (not selectedGroup)
        const filteredData = filteredLeadReportsLead.filter((LeadReport) => {
            const reportDate = new Date(LeadReport.LeadDate);
            const isWithinDateRange =
                (!probabilityFilterDateFrom ||
                    reportDate >= probabilityFilterDateFrom) &&
                (!probabilityFilterDateTo || reportDate <= probabilityFilterDateTo);

            const isWithinGroup =
                !AssignedGroup ||
                AssignedGroup === "All" ||
                getLeadGroupId(LeadReport) === AssignedGroup ||
                getLeadGroupName(LeadReport) === AssignedGroup;

            return isWithinDateRange && isWithinGroup;
        });

        aggregateProbabilityData(filteredData);
        setProbabilityDownload(filteredData);
    }, [
        filteredLeadReportsLead,
        probabilityFilterDateFrom,
        probabilityFilterDateTo,
        AssignedGroup,
        aggregateProbabilityData,
        getLeadGroupId,
        getLeadGroupName,
    ]);

    useEffect(() => {
        handleFilter(); // Automatically runs when AssignedGroup updates
    }, [handleFilter]);

    useEffect(() => {
        // Call handleFilter whenever AssignedGroup is updated after submit
        if (AssignedGroup || probabilityFilterDateFrom || probabilityFilterDateTo) {
            handleFilter();
        }
    }, [
        AssignedGroup,
        probabilityFilterDateFrom,
        probabilityFilterDateTo,
        handleFilter,
    ]);

    return (
        <div
            className="flex h-full min-h-0 w-full justify-center bg-transparent"
        >
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 pb-2">
                    <div className="flex min-w-0 flex-nowrap items-center gap-3 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => handleToggleTable()}
                            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 transition hover:bg-gray-50"
                            aria-label={showgraph ? "Collapse" : "Expand"}
                        >
                            <ChevronDown className={`h-5 w-5 text-gray-600 ${showgraph ? "" : "rotate-180"}`} />
                        </button>
                        {probabilityTab === "table" && (
                            <button
                                type="button"
                                title="Graph"
                                className="flex shrink-0 items-center justify-center rounded-full bg-blue-600 p-1 text-white transition hover:bg-blue-700"
                                onClick={() => setProbabilityTab("graph")}
                            >
                                <ChartPie className="h-5 w-5" strokeWidth={2} aria-hidden />
                            </button>
                        )}
                        {probabilityTab === "graph" && (
                            <button
                                type="button"
                                title="Table"
                                className="flex shrink-0 items-center justify-center rounded-full bg-blue-600 p-1 text-white transition hover:bg-blue-700"
                                onClick={() => setProbabilityTab("table")}
                            >
                                <TableOfContents className="h-5 w-5 rotate-180" strokeWidth={2} aria-hidden />
                            </button>
                        )}
                        <h1 className="dashboard-card-title min-w-0 truncate">
                            PROBABILITY STATUS
                        </h1>
                    </div>
                    <div className="flex shrink-0 items-center">
                        {(userLevel === "L0" ||
                            hasNoFeatureConfig ||
                            !currentEmployee ||
                            hasViewOrFullAccess(currentEmployee, "PROBABILITY STATUS DOWNLOAD")) && (
                            <div className="show-potential-download text-xl sm:text-2xl">
                                <div className="relative inline-block">
                                    <div
                                        ref={popupRef}
                                        className={`flex items-center ${probabilityStatus.length === 0 ? "pointer-events-none opacity-50" : ""} ${isDownloadViewOnly ? "pointer-events-none cursor-not-allowed text-gray-400 opacity-60" : ""}`}
                                    >
                                        <span onClick={() => sendDownlodTile()}>
                                            <DownloadReport tableLeadReports={probabilityDownload} />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {showgraph && canShowGraphs && (
                    <div className="flex min-h-0 flex-1 flex-col space-y-2 overflow-hidden pt-2">
                        {canShowFilter && (
                            <div className="shrink-0 border-b border-gray-200 pb-3">
                                <div className="flex min-w-0 flex-col gap-1.5">
                                    <div className="flex w-full min-w-0 flex-wrap items-start gap-3 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                        <div className="min-w-0 flex-1 basis-[7rem] text-left pl-1">From</div>
                                        <div className="min-w-0 flex-1 basis-[7rem] text-left pl-2">To</div>
                                        <div className="w-36 shrink-0 sm:w-40 text-left pl-5">Group</div>
                                        {/* Spacer to align with the Submit button column */}
                                        <div className="w-[6.5rem] shrink-0" aria-hidden />
                                    </div>
                                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                                        <div className="relative min-w-0 flex-1 basis-[7rem] rounded-lg border border-gray-300 bg-white">
                                            <DatePicker
                                                selected={selectedFromDate}
                                                onChange={handleProbabilityFromDate}
                                                dateFormat={getDateFormat()}
                                                popperClassName="z-[9999]"
                                                popperPlacement="bottom-start"
                                                className="h-10 w-full min-w-0 truncate border-0 bg-transparent px-3 pr-10 text-sm text-gray-800 outline-none placeholder:text-gray-400 date-picker-placeholder"
                                                placeholderText={getDatePlaceholder()}
                                                required
                                            />
                                            <Calendar
                                                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 shrink-0 text-gray-700"
                                                strokeWidth={2}
                                                aria-hidden
                                            />
                                        </div>
                                        <div className="relative min-w-0 flex-1 basis-[7rem] rounded-lg border border-gray-300 bg-white">
                                            <DatePicker
                                                selected={selectedToDate}
                                                onChange={handleProbabilityToDate}
                                                dateFormat={getDateFormat()}
                                                popperClassName="z-[9999]"
                                                popperPlacement="bottom-start"
                                                className="h-10 w-full min-w-0 truncate border-0 bg-transparent px-3 pr-10 text-sm text-gray-800 outline-none placeholder:text-gray-400 date-picker-placeholder"
                                                placeholderText={getDatePlaceholder()}
                                                required
                                                minDate={
                                                    probabilityFilterDateFrom
                                                        ? new Date(
                                                              probabilityFilterDateFrom.getTime() + 86400000
                                                          )
                                                        : null
                                                }
                                            />
                                            <Calendar
                                                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 shrink-0 text-gray-700"
                                                strokeWidth={2}
                                                aria-hidden
                                            />
                                        </div>
                                        <div
                                            ref={dropdownRefGroup}
                                            className="relative w-36 min-w-0 shrink-0 sm:w-40"
                                        >
                                            <div className="relative flex h-10 w-full items-center rounded-lg border border-gray-300 bg-white">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                                                    className="flex h-10 w-full min-w-0 items-center justify-between gap-1 truncate px-3 pr-9 text-left text-sm text-gray-800 focus:outline-none"
                                                >
                                                    <span className="truncate font-medium">
                                                        {probabilityGroupDropdownLabel}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="absolute right-2 top-1/2 z-20 -translate-y-1/2 p-0.5 text-gray-700"
                                                    onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                                                    aria-label="Toggle group"
                                                >
                                                    <ExpandMoreIcon className="!text-gray-700" fontSize="small" />
                                                </button>
                                                {showGroupDropdown && (
                                                    <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 min-w-[8rem] overflow-y-auto rounded-lg border border-black/20 bg-white py-1 text-sm shadow-lg">
                                                        <button
                                                            type="button"
                                                            className="block w-full px-3 py-1.5 text-left font-semibold transition hover:bg-gray-100"
                                                            onClick={() => handleProbabilityGroup("All")}
                                                        >
                                                            All
                                                        </button>
                                                        {groupFilterRedux && groupFilterRedux !== "All" && (
                                                            <button
                                                                type="button"
                                                                className="block w-full px-3 py-1.5 text-left transition hover:bg-gray-100"
                                                                onClick={() =>
                                                                    handleProbabilityGroup(
                                                                        groupIdByName.get(
                                                                            String(groupFilterRedux).trim()
                                                                        ) || groupFilterRedux
                                                                    )
                                                                }
                                                            >
                                                                {groupNameById.get(
                                                                    groupIdByName.get(
                                                                        String(groupFilterRedux).trim()
                                                                    ) || String(groupFilterRedux).trim()
                                                                ) || groupFilterRedux}
                                                            </button>
                                                        )}
                                                        {(Array.isArray(group) ? group : [])
                                                            .filter((groupItem) => {
                                                                const groupName = String(
                                                                    groupItem?.name ??
                                                                        groupItem?.newGroupName ??
                                                                        groupItem?.groupName ??
                                                                        ""
                                                                ).trim();
                                                                return (
                                                                    groupName !==
                                                                        String(groupFilterRedux || "").trim() &&
                                                                    groupName !== "All"
                                                                );
                                                            })
                                                            .map((groupItem) => (
                                                                <button
                                                                    type="button"
                                                                    key={groupItem?._id ?? groupItem?.id}
                                                                    className="block w-full px-3 py-1.5 text-left transition hover:bg-gray-100"
                                                                    onClick={() =>
                                                                        handleProbabilityGroup(
                                                                            String(
                                                                                groupItem?._id ??
                                                                                    groupItem?.id ??
                                                                                    ""
                                                                            )
                                                                        )
                                                                    }
                                                                >
                                                                    {groupItem?.name ??
                                                                        groupItem?.newGroupName ??
                                                                        groupItem?.groupName}
                                                                </button>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="h-10 shrink-0 whitespace-nowrap rounded-md border-2 border-gray-700 bg-white px-5 text-xs font-semibold text-[#1F2937] tracking-widest"
                                            onClick={handleSubmit}
                                        >
                                            SUBMIT
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden pb-1 pt-0">
                            {probabilityTab === "table" ? (
                                probabilityStatus.length > 0 ? (
                                    <div className="w-full rounded-lg border border-gray-200 bg-white p-2">
                                        <div className="max-h-[18rem] overflow-auto">
                                            <table className="w-full divide-y divide-gray-200 border border-gray-200 text-xs text-gray-800">
                                                <thead className="sticky top-0 z-10 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
                                                    <tr>
                                                        <th className="border-b border-gray-200 px-3 py-2 text-left">
                                                            Probability
                                                        </th>
                                                        <th className="border-b border-gray-200 px-3 py-2 text-center">
                                                            Count
                                                        </th>
                                                        <th className="border-b border-gray-200 px-3 py-2 text-center">
                                                            %
                                                        </th>
                                                        <th className="border-b border-gray-200 px-3 py-2 text-center">
                                                            Amount({currency})
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                    {probabilityStatus
                                                        .sort((a, b) => {
                                                            const probOrder = ["high", "medium", "low"];
                                                            return (
                                                                probOrder.indexOf(a.prob.toLowerCase()) -
                                                                probOrder.indexOf(b.prob.toLowerCase())
                                                            );
                                                        })
                                                        .map((item, index) => (
                                                            <tr key={index}>
                                                                <td
                                                                    onClick={() => probabilityNavigationl1(item.prob)}
                                                                    style={{ cursor: "pointer" }}
                                                                    className="border-b border-gray-200/40 px-3 py-1 text-left"
                                                                >
                                                                    {item.prob.toUpperCase()}
                                                                </td>
                                                                <td className="border-b border-gray-200/40 px-3 py-1 text-center">
                                                                    {item.count}
                                                                </td>
                                                                <td className="border-b border-gray-200/40 px-3 py-1 text-center">
                                                                    {item.percentage}
                                                                </td>
                                                                <td className="border-b border-gray-200/40 px-3 py-1 text-center">
                                                                    {USAComma(item.amount)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    <tr className="border-t border-gray-200 bg-gray-50 text-xs font-semibold">
                                                        <td className="px-3 py-2 text-left">TOTAL :</td>
                                                        <td className="px-3 py-2 text-center">
                                                            {probCalculatedCount}
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            {calculatedPercentage === "99.99"
                                                                ? 100
                                                                : calculatedPercentage}
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            {USAComma(totalProbabilityAmount)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex w-full justify-center py-6 text-center">
                                        <p className="noDataAvailable">No data available</p>
                                    </div>
                                )
                            ) : probabilityStatus.length > 0 ? (
                                <div className="flex min-h-0 flex-1 flex-col items-stretch justify-center py-2">
                                    <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-10 sm:flex-row">
                                        <div className="flex min-h-0 min-w-0 shrink-0 items-center justify-center">
                                            <ProbabilityRechartsDonut
                                                segments={segmentsForChart}
                                                onSegmentClick={probabilityNavigationl1}
                                            />
                                        </div>
                                        <div className="flex min-w-0 flex-col justify-center gap-3 text-sm text-gray-600">
                                            {legendItems.map((item) => (
                                                <div key={item.key} className="flex items-center gap-2">
                                                    {item.dotGradient ? (
                                                        <span
                                                            className="h-3 w-3 shrink-0 rounded-sm"
                                                            style={{ background: item.dotGradient }}
                                                        />
                                                    ) : (
                                                        <span
                                                            className={`h-3 w-3 shrink-0 rounded-sm ${item.dot}`}
                                                        />
                                                    )}
                                                    <span className="min-w-0">
                                                        {item.pct != null ? `${item.pct}% ` : ""}
                                                        {item.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex w-full justify-center py-6 text-center">
                                    <p className="noDataAvailable">No data available</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProbabilityStatus